import { CheckCircle2, Clock3 } from "lucide-react";
import { CategoryBadge } from "@/components/common";
import { cn } from "@/lib/utils";
import { formatPercentage } from "../../domains/formatting";
import { CATEGORY_ORDER, formatCount } from "./metrics-drawer.utils";

const buildPercentage = (completed, total) =>
  total > 0 ? (completed / total) * 100 : 0;

const CategoryProgress = ({ items }) => {
  if (!Array.isArray(items)) {
    return null;
  }

  const itemsByCategory = new Map(items.map((item) => [item.category, item]));

  return (
    <div className="space-y-3">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Data Entry Status by Category
      </h3>

      {CATEGORY_ORDER.map((category) => {
        const item = itemsByCategory.get(category) ?? {
          completed: 0,
          total: 0,
        };

        const isCompleted = item.total > 0 && item.completed === item.total;
        const percentage = buildPercentage(item.completed, item.total);

        return (
          <div
            key={category}
            className="flex items-center justify-between rounded-lg border border-border/70 bg-card px-3 py-2.5 shadow-sm transition-colors hover:border-border"
          >
            <CategoryBadge
              category={category}
              className="rounded-full px-2.5 py-1 text-[10px] font-semibold"
            />

            <div
              className={cn(
                "flex items-center gap-2",
                isCompleted
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-amber-600 dark:text-amber-400",
              )}
            >
              {isCompleted ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Clock3 className="h-4 w-4" />
              )}

              <span className="text-sm font-semibold">
                {formatPercentage(percentage)}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatCount(item.completed)} / {formatCount(item.total)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CategoryProgressSkeleton = () => (
  <div className="space-y-3">
    <div className="h-3 w-40 animate-pulse rounded bg-muted" />

    {CATEGORY_ORDER.map((category) => (
      <div
        key={category}
        className="flex items-center justify-between rounded-lg border border-border/70 bg-card px-3 py-2.5 shadow-sm"
      >
        <div className="h-7 w-28 animate-pulse rounded-full bg-muted" />
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-pulse rounded-full bg-muted" />
          <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        </div>
      </div>
    ))}
  </div>
);

const CategoryProgressWithLoading = ({ items, loading = false }) => {
  if (loading) {
    return <CategoryProgressSkeleton />;
  }

  return <CategoryProgress items={items} />;
};

export default CategoryProgressWithLoading;
